import { useEffect, useState, useMemo } from 'react';
import DefaultLayout from '../../layout/DefaultLayout';
import SectionWrapper from '../../components/SectionWrapper/SectionWrapper';
import ReportFilter, {
  UniversalFilterConfig,
} from '../../components/Filters/ReportFilter';
import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';

import { useReportType } from '../../hooks/report/useReportType';
import { useReportExport } from '../../hooks/report/useReportExport';

import { useProductCategories } from '../../hooks/useProductCategories';
import { useProductUnits } from '../../hooks/useProductUnits';
import { useProduct } from '../../hooks/useProduct';
import { useSupplier } from '../../hooks/useSupplier';
import { useBranch } from '../../hooks/branches/useBranch';
import { useDocumentType } from '../../hooks/useDocumentType';
import { usePaymentCondition } from '../../hooks/usePaymentCondition';
import { useTaxpayerCategory } from '../../hooks/useTaxpayerCategory';
import { useEconomicActivities } from '../../hooks/useEconomicActivities';

import { reportFiltersMap } from '../../features/reports/reportFiltersMap';
import { resolveReportFilters } from '../../features/reports/reportFilterResolver';
import { useCustomer } from '../../hooks/useCustomer';

const ReportPage = () => {
  const [selectedReportTypeId, setSelectedReportTypeId] = useState<
    number | null
  >(null);

  const {
    reportCode,
    handlerExportReport,
    handlerSetReportCode,
    handlerResetExportState,
  } = useReportExport();

  const { reportTypeDropdown, fetchReportTypeDropdown } = useReportType();

  const { productCategoriesDropdown, fetchProductCategoriesDropdown } =
    useProductCategories();
  const { productUnitsDropdown, fetchProductUnitsDropdown } = useProductUnits();
  const { productsDropdown, handlerGetProductsForDropdown } = useProduct();

  const { suppliersDropdown, handlerGetSuppliersForDropdown } = useSupplier();
  const { customersDropdown, handlerGetCustomersForDropdown } =
  useCustomer();
  const { branchDropdown, fetchBranchDropdown } = useBranch();
  const { documentTypeDropdown, fetchDocumentTypeDropdown } = useDocumentType();
  const { paymentConditionDropdown, fetchPaymentConditionDropdown } =
    usePaymentCondition();

  const { taxpayerCategoryDropdown, fetchTaxpayerCategoryDropdown } =
    useTaxpayerCategory();
  const { economicActivitiesDropdown, fetchEconomicActivitiesDropdown } =
    useEconomicActivities();

  useEffect(() => {
    fetchReportTypeDropdown();

    fetchProductCategoriesDropdown();
    fetchProductUnitsDropdown();
    handlerGetProductsForDropdown();

    handlerGetSuppliersForDropdown();
    handlerGetCustomersForDropdown();
    fetchBranchDropdown();
    fetchDocumentTypeDropdown();
    fetchPaymentConditionDropdown();

    fetchTaxpayerCategoryDropdown();
    fetchEconomicActivitiesDropdown();
  }, []);

  /**
   * 🔎 Filtros dinámicos según reporte
   */
  const filterFields: UniversalFilterConfig[] = useMemo(() => {
    if (!reportCode) return [];

    const baseFilters = reportFiltersMap[reportCode] || [];

    return resolveReportFilters(baseFilters, {
      productsDropdown,
      productCategoriesDropdown,
      productUnitsDropdown,
      suppliersDropdown,
      customersDropdown,
      documentTypeDropdown,
      branchDropdown,
      paymentConditionDropdown,
      taxpayerCategoryDropdown,
      economicActivitiesDropdown,
    });
  }, [
    reportCode,
    productsDropdown,
    productCategoriesDropdown,
    productUnitsDropdown,
    suppliersDropdown,
    customersDropdown,
    documentTypeDropdown,
    branchDropdown,
    paymentConditionDropdown,
    taxpayerCategoryDropdown,
    economicActivitiesDropdown,
  ]);

  const handleReportTypeChange = (id: number | null) => {
    setSelectedReportTypeId(id);

    if (!id) {
      handlerResetExportState();
      return;
    }

    const reportType = reportTypeDropdown.find((r) => r.id === id);
    if (reportType?.code) {
      handlerSetReportCode(reportType.code);
    }
  };

  return (
    <DefaultLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <ReportFilter
          availableReportTypes={reportTypeDropdown}
          selectedReportTypeId={selectedReportTypeId}
          onReportTypeChange={handleReportTypeChange}
          onResetFilters={handlerResetExportState}
          onGenerateReport={handlerExportReport}
          filtersConfig={filterFields}
        />
      </SectionWrapper>
    </DefaultLayout>
  );
};

export default ReportPage;
